import { LoadingLayout, PageLayout, Text } from "components";
import { UserContext } from "context";
import { useContext, useState } from "react";
import { format } from "date-fns";
import { EActivityEvent, E_AssistantActions, T_AssistantLogs } from "types";
import { ActivityDetailsDialog, StatCard } from "../components";
import { useActivity } from "../hooks";
import { ActivityContainer, ActivityList, StatsContainer } from "../styles";
import { ActivityItem } from "../components/activityItem";
import { usePageViewed } from "hooks";

export const ActivityScreen = () => {
  const { logs, loading } = useActivity();

  const [selected, setSelected] = useState<T_AssistantLogs>();
  // const [page, setPage] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const { state: userState } = useContext(UserContext);

  usePageViewed(EActivityEvent.ActivityLogViewed);

  const archivedLogsCount = (index: number) => {
    return (
      logs?.[index]?.items?.filter((item) =>
        item.actions?.includes(E_AssistantActions.Archived),
      )?.length || 0
    );
  };

  const seeArchivedInGmail = (startDate: Date, endDate?: Date) => {
    const labelFilter = `-in:drafts+-in:trash+-in:spam+-in:inbox+-in:sent+-label:@${userState?.firstName?.trim()?.toLowerCase() || "me"}`;
    let dateFilter = `after:${format(new Date(startDate), "yyyy/MM/dd")}`;
    if (endDate) {
      dateFilter += `+before:${format(new Date(endDate), "yyyy/MM/dd")}`;
    }
    dateFilter = dateFilter.replace(/\//g, "%2F"); // replace / with %2F to make it work in url
    const url = `https://mail.google.com/mail/u/0/#search/${labelFilter}+${dateFilter}`;
    window.open(url, "_blank");
  };

  const seeDraftsInGmail = (startDate: Date, endDate?: Date) => {
    const labelFilter = "label:ai-draft";
    let dateFilter = `after:${format(new Date(startDate), "yyyy/MM/dd")}`;
    if (endDate) {
      dateFilter += `+before:${format(new Date(endDate), "yyyy/MM/dd")}`;
    }
    dateFilter = dateFilter.replace(/\//g, "%2F"); // replace / with %2F to make it work in url
    const url = `https://mail.google.com/mail/u/0/#search/${labelFilter}+${dateFilter}`;
    //const url = `https://mail.google.com/mail/u/0/#label/AI+draft`;
    window.open(url, "_blank");
  };

  //const meetingsCount = (index: number) => {
  //  return (
  //    logs?.[index]?.items?.filter((item) =>
  //      item.actions?.includes(E_AssistantActions.MeetingPrepDone),
  //    )?.length || 0
  //  );
  //};

  const draftCount = (index: number) => {
    return (
      logs?.[index]?.items?.filter((item) =>
        item.actions?.includes(E_AssistantActions.EmailDrafted),
      )?.length || 0
    );
  };

  const countTotal = (index: number) => {
    // todo: remove duplicate emails by id
    return logs?.[index]?.items?.length || 0;
  };

  const handleSelect = (item: T_AssistantLogs) => {
    setSelected(item);
    setShowDetails(true);
  };

  if (loading) {
    return <LoadingLayout />;
  }

  return (
    <PageLayout title="Activity Log | Multiplayer">
      <div className={ActivityContainer}>
        {!loading && logs?.length === 0 && (
          <Text.Body>
            {" "}
            Here you will see every action Multiplayer AI is taking on your
            behalf.
          </Text.Body>
        )}
        {logs?.map((log, idx) => (
          <div key={log._id} className={ActivityList}>
            <Text.BodySM color="seconday">
              {format(
                new Date(log?.items[0].created || log?._id),
                "EEEE, MMMM do",
              )}
            </Text.BodySM>
            <div className={StatsContainer}>
              <StatCard
                title1="EMAILS"
                title2="ARCHIVED"
                value1={`${archivedLogsCount(idx)}/${countTotal(idx)}`}
                value2={`${getPercentage(archivedLogsCount(idx), countTotal(idx), 0)}%`}
                onClick={() =>
                  seeArchivedInGmail(
                    new Date(log?.items[0].created || log?._id),
                    idx > 0
                      ? new Date(
                          logs[idx - 1]?.items[0].created || logs[idx - 1]?._id,
                        )
                      : undefined,
                  )
                }
              />
              <StatCard
                title1="EMAIL DRAFTS"
                title2="WRITTEN"
                value1={draftCount(idx).toString()}
                onClick={() =>
                  seeDraftsInGmail(
                    new Date(log?.items[0].created || log?._id),
                    idx > 0
                      ? new Date(
                          logs[idx - 1]?.items[0].created || logs[idx - 1]?._id,
                        )
                      : undefined,
                  )
                }
              />
            </div>

            {log.items.map((item) => (
              <ActivityItem
                key={item._id}
                item={item}
                onSelect={handleSelect}
              />
            ))}
          </div>
        ))}

        <ActivityDetailsDialog
          open={showDetails}
          onOpenChange={setShowDetails}
          item={selected}
        />
      </div>
    </PageLayout>
  );
};

function getPercentage(value: number, total: number, precision = 2) {
  if (total === 0) {
    return 0;
  }
  return ((value / total) * 100).toFixed(precision);
}
